/* What order the phone's board reads in.
 *
 * The grid has no such question: its columns are laid out left to right in
 * whatever order the salon dragged them into, and that order is a fact about
 * the room — who sits where. A list four inches wide cannot show that and
 * would not be read for it if it could. What it is read for is "who is up",
 * top to bottom, which is a different question and has a different answer.
 *
 * Three bands, and the bands matter more than the sort inside them:
 *
 *   0  free        the only people a walk-in can actually be given to. The
 *                  first row of this band is who is next up, which is the one
 *                  thing the list exists to say.
 *   1  in service  they rejoin the rotation the moment they stand up, so they
 *                  are ordered among themselves the same way — the list is
 *                  then also a reading of who is up *after* this round.
 *   2  out         skipped, on a break, done for the day, or out of day — the
 *                  hours on their name have run out and there is no longer room
 *                  before they go for anything they do. Last, because they are
 *                  not part of the next answer at all, and mixing them in on
 *                  rank alone put somebody who had gone home at the top of the
 *                  board — they had taken the fewest turns.
 *
 * Inside a band it is the rotation's own rank, which is credit and idle time
 * worked out over everyone in today. `rank` is that, as staff id -> position;
 * anyone missing from it sorts after everyone who is in it rather than at the
 * top, which is what a bare `undefined` would have done.
 *
 * Ties break on the board's own order — the index the column already had — so
 * the list of a floor where nobody has done anything yet is the board read
 * left to right, and not a reshuffle every time the day's first turn lands.
 */

export function bandOf(col) {
  // Two ways to be out and one band for them, because the list is read for one
  // question — can this person take the client at the desk — and the answer is
  // no either way. What KIND of no is the badge's business, not the order's.
  // Own clients only is a no for a walk-in too: they take who asks for them,
  // and the list is read for who takes the client nobody asked for.
  if (col.skipped || col.offShift || col.ownClients) return 2;
  if (col.busy) return 1;
  return 0;
}

// Sorted copy. The board's array is a computed and belongs to whoever built
// it; sorting it in place would reorder the grid as a side effect of drawing
// the list, which is a bug that only shows up on a phone turned sideways.
export function queueOrder(columns, rank) {
  var ranks = rank || {};
  return columns
    .map(function (col, i) {
      var r = ranks[col.staff.id];
      return {
        col: col,
        i: i,
        band: bandOf(col),
        rank: typeof r === 'number' ? r : Infinity
      };
    })
    .sort(function (a, b) {
      return a.band - b.band || a.rank - b.rank || a.i - b.i;
    })
    .map(function (x) { return x.col; });
}

// Who the top of the list is about, or null when nobody on the floor is free.
// Read for the UP NEXT badge: the badge is a claim about a person, so it must
// come from the same pass that put them at the top and not from a second one
// that could disagree with it.
export function headOfQueue(ordered) {
  if (!ordered.length) return null;
  var first = ordered[0];
  return bandOf(first) === 0 ? first.staff.id : null;
}
